import {
  phoneLast4FromE164,
  TwoFactorChallenge,
} from "@/utils/twoFactor/twoFactor";

export const MFA_CHALLENGE_MISSING_MESSAGE =
  "We could not start phone verification. Please try signing in to continue.";

export type SignupMfaResponse = {
  success?: boolean;
  two_factor?: boolean | number;
  phone_required?: boolean | number;
  token?: string;
  phone_last4?: string;
  err?: string;
};

export type SignupMfaResult =
  | { handled: false }
  | { handled: true; challenge: TwoFactorChallenge };

const phoneLast4FromResponse = (
  signupResponse: SignupMfaResponse,
  phone: string,
): string => {
  if (
    signupResponse.phone_last4 != null &&
    String(signupResponse.phone_last4).length > 0
  ) {
    return String(signupResponse.phone_last4)
      .replace(/\D/g, "")
      .slice(-4);
  }
  return phoneLast4FromE164(phone);
};

export const isSignupMfaChallenge = (
  signupResponse: SignupMfaResponse | null | undefined,
): boolean => Boolean(signupResponse?.two_factor && signupResponse?.token);

export const handleSignupMfaResponse = async ({
  signupResponse,
  phone,
  sendCode,
}: {
  signupResponse: SignupMfaResponse;
  phone: string;
  sendCode: (
    token: string,
    phone: string,
  ) => Promise<SignupMfaResponse | null | undefined>;
}): Promise<SignupMfaResult> => {
  if (!isSignupMfaChallenge(signupResponse) || !signupResponse.token) {
    return { handled: false };
  }

  const oathTokenPath =
    Boolean(signupResponse.phone_required) || signupResponse.success === false;

  if (oathTokenPath) {
    const sendRes = await sendCode(signupResponse.token, phone);
    if (sendRes?.success && sendRes?.token) {
      return {
        handled: true,
        challenge: {
          temporaryToken: sendRes.token,
          oathToken: signupResponse.token,
          phone,
          phoneLast4: phoneLast4FromResponse(sendRes, phone),
          skipInitialCooldown: false,
          initialError: "",
        },
      };
    }

    return {
      handled: true,
      challenge: {
        temporaryToken: "",
        oathToken: signupResponse.token,
        phone,
        phoneLast4: phoneLast4FromE164(phone),
        skipInitialCooldown: true,
        initialError:
          sendRes?.err ||
          signupResponse.err ||
          "We could not send your verification code. Tap Resend to try again.",
      },
    };
  }

  return {
    handled: true,
    challenge: {
      temporaryToken: signupResponse.token,
      oathToken: "",
      phone,
      phoneLast4: phoneLast4FromResponse(signupResponse, phone),
      skipInitialCooldown: false,
      initialError: "",
    },
  };
};
